import React, { useState } from 'react';
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
} from '@mui/material';
import {
  Tv as TvIcon,
  Videocam as StreamIcon,
} from '@mui/icons-material';
import ProjectCard from './ProjectCard';
import ProjectModal from './ProjectModal';
import FlippeAndGoSleepProject from './projects/FlippeAndGoSleepProject';
import TouchePasAMonMateProject from './projects/TouchePasAMonMateProject';
import Eloguessr from './projects/Eloguessr';
import MaghlaProject from './projects/MaghlaProject';
import QVGDPLOIOProject from './projects/QVGDPLOIOProject';
import BoiteAmineProject from './projects/BoiteAmineProject';
import QuizzJDGProject from './projects/QuizzJDGProject';
import QPUSProject from './projects/QPUSProject';
import CrazyTownProject from './projects/CrazyTownProject';
import DouxeCoupsDeMidiProject from './projects/DouxeCoupsDeMidiProject';
import DALSProject from './projects/DALSProject';
import MaisonLumniProject from './projects/MaisonLumniProject';
import MotDePasseProject from './projects/MotDePasseProject';
import ElectionsMunicipalesProject from './projects/ElectionsMunicipalesProject';
import TheVoiceProject from './projects/TheVoiceProject';
import TLMASMADProject from './projects/TLMASMADProject';
import TLMVPSPProject from './projects/TLMVPSPProject';

// Interface pour les données de projet
interface ProjectData {
  id: number;
  name: string;
  imageUrl: string;
  category: 'twitch' | 'tv';
}

// Type pour les catégories
type ProjectCategory = 'twitch' | 'tv';

// Configuration des catégories
const categoryConfig = {
  twitch: {
    label: 'Twitch',
    icon: StreamIcon,
    gradient: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
    folder: 'Twitch'
  },
  tv: {
    label: 'TV',
    icon: TvIcon,
    gradient: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
    folder: 'TV'
  }
};

// Données des projets par catégorie
const projectsData: ProjectData[] = [
  // Projets Twitch
  { id: 1, name: 'Flippe And Go Sleep', imageUrl: '/fog.png', category: 'twitch' },
  { id: 2, name: 'Touche pas à mon mate', imageUrl: '/TouchePasAMonMate.png', category: 'twitch' },
  { id: 3, name: 'Eloguessr', imageUrl: '/eloguessr.png', category: 'twitch' },
  { id: 4, name: 'Maghla', imageUrl: '/RefMenyaLogo.png', category: 'twitch' },
  { id: 5, name: 'Qui veut gagner des places pour RedBull League Of It\'s Own', imageUrl: '/QVGDPLOIO.png', category: 'twitch' },
  { id: 6, name: 'La Boîte d\'Amine', imageUrl: '/BoiteAmine.png', category: 'twitch' },
  { id: 7, name: 'Quizz JDG', imageUrl: '/QuizzJDG.png', category: 'twitch' },
  { id: 8, name: 'Questions pour un streamer', imageUrl: '/QPUS.png', category: 'twitch' },
  { id: 9, name: 'CrazyTown', imageUrl: '/CrazyTown.png', category: 'twitch' },

  // Projets TV
  { id: 11, name: '12 Coups de Midi', imageUrl: '/12CDM.png', category: 'tv' },
  { id: 12, name: 'Danse avec les Stars', imageUrl: '/DALS.png', category: 'tv' },
  { id: 13, name: 'La Maison Lumni', imageUrl: '/MaisonLumni.png', category: 'tv' },
  { id: 14, name: 'Mot de Passe', imageUrl: '/MDP.png', category: 'tv' },
  { id: 15, name: 'Elections Municipales 2020', imageUrl: '/TF1EM.png', category: 'tv' },
  { id: 16, name: 'The Voice', imageUrl: '/The_Voice.png', category: 'tv' },
  { id: 17, name: 'Tout le monde a son mot à dire', imageUrl: '/TLMASMAD.png', category: 'tv' },
  { id: 19, name: 'Tout le monde veut prendre sa place', imageUrl: '/TLMVPSP.png', category: 'tv' },
];

interface ProjectsProps {
  onModalStateChange?: (isOpen: boolean) => void;
}

const Projects: React.FC<ProjectsProps> = ({ onModalStateChange }) => {
  const [selectedCategory, setSelectedCategory] = useState<ProjectCategory>('twitch');
  const [selectedProjectComponent, setSelectedProjectComponent] = useState<React.ComponentType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filtrer les projets selon la catégorie sélectionnée
  const filteredProjects = projectsData.filter(project => project.category === selectedCategory);

  // Configuration de la catégorie actuelle
  const currentConfig = categoryConfig[selectedCategory];

  const handleCategoryChange = (category: ProjectCategory) => {
    setSelectedCategory(category);
  };

  const handleProjectSelect = (project: ProjectData) => {
    // Mapper chaque projet à son composant spécifique
    let ProjectComponent: React.ComponentType;

    switch (project.name) {
      case 'Flippe And Go Sleep':
        ProjectComponent = FlippeAndGoSleepProject;
        break;
      case 'Touche pas à mon mate':
        ProjectComponent = TouchePasAMonMateProject;
        break;
      case 'Eloguessr':
        ProjectComponent = Eloguessr;
        break;
      case 'Maghla':
        ProjectComponent = MaghlaProject;
        break;
      case 'Qui veut gagner des places pour RedBull League Of It\'s Own':
        ProjectComponent = QVGDPLOIOProject;
        break;
      case 'La Boîte d\'Amine':
        ProjectComponent = BoiteAmineProject;
        break;
      case 'Quizz JDG':
        ProjectComponent = QuizzJDGProject;
        break;
      case 'Questions pour un streamer':
        ProjectComponent = QPUSProject;
        break;
      case 'CrazyTown':
        ProjectComponent = CrazyTownProject;
        break;
      case '12 Coups de Midi':
        ProjectComponent = DouxeCoupsDeMidiProject;
        break;
      case 'Danse avec les Stars':
        ProjectComponent = DALSProject;
        break;
      case 'La Maison Lumni':
        ProjectComponent = MaisonLumniProject;
        break;
      case 'Mot de Passe':
        ProjectComponent = MotDePasseProject;
        break;
      case 'Elections Municipales 2020':
        ProjectComponent = ElectionsMunicipalesProject;
        break;
      case 'The Voice':
        ProjectComponent = TheVoiceProject;
        break;
      case 'Tout le monde a son mot à dire':
        ProjectComponent = TLMASMADProject;
        break;
      case 'Tout le monde veut prendre sa place':
        ProjectComponent = TLMVPSPProject;
        break;
    }

    setSelectedProjectComponent(() => ProjectComponent);
    setIsModalOpen(true);
    onModalStateChange?.(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProjectComponent(null);
    onModalStateChange?.(false);
  };

  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: { xs: 'flex-start', md: 'center' }, // Centrage sur desktop restauré
        height: { xs: 'auto', md: '100%' }, // Hauteur auto sur desktop
      }}
      aria-labelledby="projects-title"
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header avec titre et navigation */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 'bold',
              mb: 3,
              textAlign: 'center',
            }}
          >
            Mes Projets
          </Typography>

          {/* ButtonGroup pour les catégories */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ButtonGroup
              variant="outlined"
              size="large"
              sx={{
                '& .MuiButtonGroup-grouped': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  px: 3,
                  py: 1.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                  },
                  '&.active': {
                    background: currentConfig.gradient,
                    borderColor: 'transparent',
                    color: 'white',
                    '&:hover': {
                      background: currentConfig.gradient,
                      opacity: 0.9,
                    },
                  },
                },
              }}
              aria-label="Catégories de projets"
            >
              {(Object.keys(categoryConfig) as ProjectCategory[]).map((category) => {
                const config = categoryConfig[category];
                const IconComponent = config.icon;
                const isActive = selectedCategory === category;

                return (
                  <Button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    startIcon={<IconComponent />}
                    className={isActive ? 'active' : ''}
                    aria-pressed={isActive}
                    sx={{
                      minWidth: 120,
                      ...(isActive && {
                        background: config.gradient,
                        borderColor: 'transparent !important',
                        color: 'white !important',
                        '&:hover': {
                          background: config.gradient,
                          opacity: 0.9,
                        },
                      }),
                    }}
                  >
                    {config.label}
                  </Button>
                );
              })}
            </ButtonGroup>
          </Box>
        </Box>

        {/* Zone scrollable des projets */}
        <Box
          sx={{
            flex: 1,
            overflowY: { xs: 'visible', md: 'auto' }, // Scroll uniquement sur desktop
            overflowX: 'visible', // Permet aux effets de glow de déborder sur les côtés
            maxHeight: { xs: 'none', md: '80vh' }, // Hauteur maximale optimisée pour écran 1080p (3 lignes de projets)
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: currentConfig.gradient,
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(167, 139, 250, 0.8)',
              },
            },
          }}
        >
          {/* Grid des projets */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 3,
              p: 4 // Plus de padding pour l'effet de glow des cartes
            }}
          >
            {filteredProjects.map((project) => (
              <Box key={project.id}>
                <ProjectCard
                  imageUrl={`/${currentConfig.folder}${currentConfig.folder ? '/' : ''}${project.imageUrl}`}
                  name={project.name}
                  onSelect={() => handleProjectSelect(project)}
                />
              </Box>
            ))}
          </Box>

          {/* Message si aucun projet */}
          {filteredProjects.length === 0 && (
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                mt: 4,
                textAlign: 'center',
              }}
            >
              Projets à venir dans cette catégorie...
            </Typography>
          )}
        </Box>
      </Box>

      {/* Modal des projets */}
      <ProjectModal
        open={isModalOpen}
        onClose={handleCloseModal}
        projectComponent={selectedProjectComponent}
      />
    </Box>
  );
};

export default Projects;
